/**
 * Date helpers that stay in the user's local timezone.
 *
 * Plain `new Date("2026-05-15")` is parsed as UTC midnight, which renders as
 * the previous day in negative-offset zones. The helpers below treat
 * date-only strings ("YYYY-MM-DD") and "YYYY-MM-DD HH:MM:SS" (no `T`, no
 * offset) as local-wall-clock times.
 */

export const parseLocalDate = (s: string | null | undefined): Date | null => {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};

export const formatLocalDate = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/**
 * Pin any Date to LOCAL midnight of its local calendar day. Use as a date
 * picker's onChange normalizer for date-only fields so the stored value's day
 * always matches what the user sees, regardless of the picker's anchoring.
 *
 * Accepts a string too: Mantine 8's DateInput onChange hands back a
 * "YYYY-MM-DD" string (not a Date) once you type in the field, so this
 * helper has to handle both shapes or it crashes with
 * "d.getHours is not a function" on typed input.
 */
export const normalizeToLocalMidnight = (
  d: Date | string | null,
): Date | null => {
  if (!d) return null;
  if (typeof d === "string") {
    return parseLocalDate(d);
  }
  // Calendar picks arrive at LOCAL midnight; typed input can arrive anchored
  // at UTC midnight, which reads as the previous day in negative-offset zones
  // (the "12/19 -> 12/18" flip). Detect the UTC-anchored typed case via a
  // non-midnight local time and recover the intended day from UTC parts;
  // otherwise use the local parts of the already-local-midnight value.
  const atLocalMidnight =
    d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0;
  return atLocalMidnight
    ? new Date(d.getFullYear(), d.getMonth(), d.getDate())
    : new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

const MONTHS_LONG = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/**
 * Format a date-only "YYYY-MM-DD" string as "Month D, YYYY" by reading the
 * string parts directly — never via a Date/timezone. A date-only value (e.g.
 * a birthday) must render identically in every timezone, so no `new Date()`
 * or UTC math is involved.
 */
export const formatIsoDateLong = (s: string | null | undefined): string => {
  if (!s) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return s;
  const year = Number(m[1]);
  const monthIdx = Number(m[2]) - 1;
  const day = Number(m[3]);
  if (monthIdx < 0 || monthIdx > 11) return s;
  return `${MONTHS_LONG[monthIdx]} ${day}, ${year}`;
};

/**
 * Compute whole-years age from a date-only "YYYY-MM-DD" string using string
 * parts (no Date round-trip on the stored value). Returns null if unparseable.
 */
export const ageFromIsoDate = (s: string | null | undefined): number | null => {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const now = new Date();
  let age = now.getFullYear() - year;
  const beforeBirthday =
    now.getMonth() + 1 < month ||
    (now.getMonth() + 1 === month && now.getDate() < day);
  if (beforeBirthday) age--;
  return age;
};

/**
 * Parse server-emitted timestamps that may be either full ISO
 * ("2026-05-15T13:00:00Z") or space-separated naive
 * ("2026-05-15 13:00:00"). Date-only strings are anchored to local midnight
 * to avoid TZ rollover.
 *
 * Server timestamps are UTC. A datetime with a time component but NO timezone
 * designator (no trailing `Z`, no `±HH:MM` offset) would be parsed by
 * `new Date()` as LOCAL time and misread by the viewer's offset — so we pin
 * it to UTC. Offset-aware strings are left untouched.
 */
const HAS_TZ_OFFSET = /([zZ]|[+-]\d{2}:?\d{2})$/;

export const parseServerDateTime = (
  s: string | null | undefined,
): Date | null => {
  if (!s) return null;
  const normalized = s.includes("T") ? s : s.replace(" ", "T");
  const toParse =
    normalized.includes("T") && !HAS_TZ_OFFSET.test(normalized)
      ? `${normalized}Z`
      : normalized;
  const d = new Date(toParse);
  if (!Number.isNaN(d.getTime())) return d;
  return parseLocalDate(s);
};

/**
 * Whole-day diff between two timestamps that ignores DST flips by snapping
 * each side to local midnight before subtracting. Returns days(to - from);
 * negative if `to` is in the past.
 */
export const wholeDayDiff = (from: Date, to: Date): number => {
  const a = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const b = new Date(to.getFullYear(), to.getMonth(), to.getDate());
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
};
